// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Sagsdata as data: the fields of the case form, the draft it edits, the
 * sparse PATCH a save sends, the BEK 496 note and the plan deadline.
 *
 * The record is `ProjectInfo` (name, address, year, survey date and
 * organisation, notes — the old Overblik editor's fields, reusing
 * `META_FIELDS` / `metaPatch` / `yearCommit`) PLUS the BEK 496 fields
 * (CONTRACT-GAPS gap 3, closed by ruxd-6tz): `bbr_number, cadastral, client,
 * client_contact, coordinator, municipality, demolition_start,
 * demolished_floor_area_m2, screening_approved_at`. They are sent in the
 * same PATCH, and ruxd returns every one (null when unset), so the server
 * is the only copy. An older ruxd drops them: when the response does not
 * carry a key, the value is kept in localStorage per project id
 * (`extrasStorageKey`) and the form says "ikke gemt af serveren endnu". A key
 * the server returns — even as null — always wins over the local copy, and
 * a value kept locally before the server learnt the key is offered back to
 * the form (`leftoverExtras`) so one Gem moves it to the server.
 *
 * Pure: no React, no DOM (storage is passed in).
 */

import type { ProjectInfo } from '../api/types';
import { formatNumber } from '../kortlaegning/vocab';
import {
  EMPTY_NAME_TOAST,
  INVALID_YEAR_TOAST,
  META_FIELDS,
  metaPatch,
  yearCommit,
  yearText,
  type MetaField,
  type ProjectPatch,
} from '../overblik/model';

// ------------------------------------------------------------- the extras --

/** The BEK 496 fields (gap 3; stored by ruxd since ruxd-6tz), in form order. */
export const EXTRA_KEYS = [
  'bbr_number',
  'cadastral',
  'client',
  'client_contact',
  'coordinator',
  'municipality',
  'demolition_start',
  'demolished_floor_area_m2',
  'screening_approved_at',
] as const;

export type ExtraKey = (typeof EXTRA_KEYS)[number];

const DATE_EXTRAS: ReadonlySet<ExtraKey> = new Set(['demolition_start', 'screening_approved_at']);
const NUMBER_EXTRAS: ReadonlySet<ExtraKey> = new Set(['demolished_floor_area_m2']);

/** The `ProjectInfo` Sagsdata fields as the form holds them. Dates are ISO `YYYY-MM-DD`. */
export interface SagsdataExtras {
  bbr_number?: string;
  cadastral?: string;
  client?: string;
  client_contact?: string;
  coordinator?: string;
  municipality?: string;
  demolition_start?: string;
  demolished_floor_area_m2?: number;
  screening_approved_at?: string;
}

/** A sparse write of the extras; null clears. */
export type ExtrasPatch = { [K in ExtraKey]?: SagsdataExtras[K] | null };

/** What a save sends: the contract's `ProjectPatch` plus the Sagsdata fields. */
export type SagsPatch = ProjectPatch & ExtrasPatch;

export function isExtraKey(key: string): key is ExtraKey {
  return (EXTRA_KEYS as readonly string[]).includes(key);
}

// --------------------------------------------------------------- the form --

export type SagsKey = MetaField | 'year_of_construction' | ExtraKey;
export type FieldKind = 'text' | 'multiline' | 'year' | 'date' | 'number';

/** The groups the fields fall into: the editor's fieldsets and the Sagen panel's lists. */
export type SagsGroupId = 'sag' | 'bygning' | 'personer' | 'nedrivning' | 'noter';

/** The groups in order, with their titles (the editor's legends). */
export const SAGSDATA_GROUPS: readonly { id: SagsGroupId; title: string }[] = [
  { id: 'sag', title: 'Sag' },
  { id: 'bygning', title: 'Bygning' },
  { id: 'personer', title: 'Personer' },
  { id: 'nedrivning', title: 'Nedrivning · BEK 496' },
  { id: 'noter', title: 'Noter' },
];

export interface SagsFieldSpec {
  key: SagsKey;
  label: string;
  kind: FieldKind;
  group: SagsGroupId;
  /** The case name: can never be emptied. */
  required?: boolean;
  /** A gap-3 field: an older server cannot store it, so it may be kept in this browser. */
  stub?: boolean;
  /** Shown after a number field. */
  unit?: string;
  placeholder?: string;
}

function metaLabel(key: MetaField): string {
  return META_FIELDS.find((f) => f.key === key)?.label ?? key;
}

/**
 * The form's fields in order (prototype v8's Sagsdata, plus the old Overblik
 * editor's fields), grouped: each group's fields are contiguous and the
 * groups follow `SAGSDATA_GROUPS`.
 */
export const SAGSDATA_FIELDS: readonly SagsFieldSpec[] = [
  { key: 'name', label: metaLabel('name'), kind: 'text', group: 'sag', required: true },
  { key: 'survey_date', label: metaLabel('survey_date'), kind: 'date', group: 'sag' },
  { key: 'survey_organisation', label: metaLabel('survey_organisation'), kind: 'text', group: 'sag' },
  { key: 'building_address', label: metaLabel('building_address'), kind: 'text', group: 'bygning', placeholder: 'fx Skolevej 12, 6000 Kolding' },
  { key: 'bbr_number', label: 'BBR-nummer', kind: 'text', group: 'bygning', stub: true, placeholder: 'fx 621-123456-1' },
  { key: 'cadastral', label: 'Matrikel', kind: 'text', group: 'bygning', stub: true, placeholder: 'fx 12a Kolding Bygrunde' },
  { key: 'municipality', label: 'Kommune', kind: 'text', group: 'bygning', stub: true },
  { key: 'year_of_construction', label: 'Opført (år)', kind: 'year', group: 'bygning', placeholder: 'fx 1978' },
  { key: 'client', label: 'Bygherre', kind: 'text', group: 'personer', stub: true },
  { key: 'client_contact', label: 'Kontaktperson', kind: 'text', group: 'personer', stub: true },
  { key: 'coordinator', label: 'Miljø- og ressourcekoordinator', kind: 'text', group: 'personer', stub: true },
  { key: 'demolition_start', label: 'Nedrivning starter', kind: 'date', group: 'nedrivning', stub: true },
  {
    key: 'demolished_floor_area_m2',
    label: 'Nedrevet etageareal',
    kind: 'number',
    group: 'nedrivning',
    unit: 'm²',
    stub: true,
    placeholder: 'fx 312',
  },
  { key: 'screening_approved_at', label: 'Screening godkendt af bygherre', kind: 'date', group: 'nedrivning', stub: true },
  { key: 'notes', label: metaLabel('notes'), kind: 'multiline', group: 'noter' },
];

export function fieldSpec(key: SagsKey): SagsFieldSpec | undefined {
  return SAGSDATA_FIELDS.find((f) => f.key === key);
}

/** What every field shows, as text (the form edits strings). */
export type SagsDraft = Record<SagsKey, string>;

/** `2026-11-30` (a real calendar date); anything else is not. */
export function isIsoDate(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

/**
 * The area field's validator: empty → clear (null); a non-negative number
 * with `.` or `,` as the decimal mark → that; anything else is invalid.
 */
export function parseArea(text: string): { value: number | null } | null {
  const t = text.trim();
  if (t === '') return { value: null };
  if (!/^\d+([.,]\d+)?$/.test(t)) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? { value: n } : null;
}

/**
 * Whether a date field can be a native date input: a legacy `survey_date`
 * stored as free text (not ISO) is edited as text, so it is never blanked.
 */
export function dateInputFits(stored: string): boolean {
  return stored.trim() === '' || isIsoDate(stored);
}

/** The draft for a record and its extras. */
export function draftOf(project: ProjectInfo | null | undefined, extras: SagsdataExtras): SagsDraft {
  const out = {} as SagsDraft;
  for (const spec of SAGSDATA_FIELDS) {
    const key = spec.key;
    if (key === 'year_of_construction') out[key] = yearText(project?.year_of_construction);
    else if (key === 'demolished_floor_area_m2') {
      const n = extras.demolished_floor_area_m2;
      out[key] = n === undefined ? '' : String(n);
    } else if (isExtraKey(key)) out[key] = (extras[key] as string | undefined) ?? '';
    else out[key] = project?.[key] ?? '';
  }
  return out;
}

function sameValue(spec: SagsFieldSpec, a: string, b: string): boolean {
  if (spec.kind === 'number') {
    const pa = parseArea(a);
    const pb = parseArea(b);
    if (pa && pb) return pa.value === pb.value;
  }
  return a.trim() === b.trim();
}

/** Fields whose draft differs from the stored value (a number by its parsed value). */
export function changedKeys(draft: SagsDraft, base: SagsDraft): SagsKey[] {
  return SAGSDATA_FIELDS.filter((s) => !sameValue(s, draft[s.key], base[s.key])).map((s) => s.key);
}

export function isDirty(draft: SagsDraft, base: SagsDraft): boolean {
  return changedKeys(draft, base).length > 0;
}

export const INVALID_DATE_TEXT = 'Datoen er ikke gyldig.';
export const INVALID_AREA_TEXT = 'Arealet skal være et tal i m², fx 312.';

export interface BuiltPatch {
  /** Only the changed fields; empty when nothing changed. */
  patch: SagsPatch;
  /** Why a field cannot be saved; non-empty means nothing may be sent. */
  errors: Partial<Record<SagsKey, string>>;
}

/**
 * The sparse PATCH for the changed fields. Text is trimmed and an emptied
 * optional field is cleared with null (`metaPatch`); the name can never be
 * emptied; the year goes through `yearCommit`; dates must be real ISO dates;
 * the area must parse.
 */
export function buildPatch(draft: SagsDraft, base: SagsDraft, now: Date = new Date()): BuiltPatch {
  const patch: Record<string, unknown> = {};
  const errors: Partial<Record<SagsKey, string>> = {};
  for (const key of changedKeys(draft, base)) {
    const spec = fieldSpec(key);
    if (!spec) continue;
    const value = draft[key].trim();
    if (key === 'year_of_construction') {
      const current = base[key] === '' ? undefined : Number(base[key]);
      const c = yearCommit(draft[key], current, now.getFullYear() + 1);
      if (c.send) patch[key] = c.value;
      else if (c.invalid) errors[key] = INVALID_YEAR_TOAST;
      continue;
    }
    if (isExtraKey(key)) {
      if (NUMBER_EXTRAS.has(key)) {
        const parsed = parseArea(value);
        if (parsed === null) errors[key] = INVALID_AREA_TEXT;
        else patch[key] = parsed.value;
      } else if (DATE_EXTRAS.has(key)) {
        if (value === '') patch[key] = null;
        else if (isIsoDate(value)) patch[key] = value;
        else errors[key] = INVALID_DATE_TEXT;
      } else {
        patch[key] = value === '' ? null : value;
      }
      continue;
    }
    // A `ProjectInfo` text field (survey_date included: a date input only
    // ever yields ISO or '', and a legacy free-text date stays free text).
    if (key === 'name' && value === '') {
      errors[key] = EMPTY_NAME_TOAST;
      continue;
    }
    Object.assign(patch, metaPatch(key, value));
  }
  return { patch: patch as SagsPatch, errors };
}

/** The extras part of a patch. */
export function extrasOf(patch: SagsPatch): ExtrasPatch {
  const out: Record<string, unknown> = {};
  for (const key of EXTRA_KEYS) if (key in patch) out[key] = patch[key];
  return out as ExtrasPatch;
}

// ------------------------------------------------ server vs. local copies --

/** Whether the server's record carries `key` at all (null counts: it is stored, empty). */
export function serverHas(project: ProjectInfo | null | undefined, key: ExtraKey): boolean {
  return !!project && Object.prototype.hasOwnProperty.call(project, key);
}

function extraValue(key: ExtraKey, raw: unknown): string | number | undefined {
  if (NUMBER_EXTRAS.has(key)) return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : undefined;
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  if (DATE_EXTRAS.has(key)) {
    if (isIsoDate(raw)) return raw;
    // ruxd returns `screening_approved_at` as sent: a date-time reads as its date.
    const head = raw.trim().slice(0, 10);
    return key === 'screening_approved_at' && /^\d{4}-\d{2}-\d{2}T/.test(raw.trim()) && isIsoDate(head) ? head : undefined;
  }
  return raw;
}

function setExtra(out: SagsdataExtras, key: ExtraKey, value: string | number | undefined): void {
  if (value === undefined) delete out[key];
  else Object.assign(out, { [key]: value });
}

/** The extras the server returned (well-typed values only). */
export function serverExtras(project: ProjectInfo | null | undefined): SagsdataExtras {
  const out: SagsdataExtras = {};
  if (!project) return out;
  const rec = project as unknown as Record<string, unknown>;
  for (const key of EXTRA_KEYS) if (serverHas(project, key)) setExtra(out, key, extraValue(key, rec[key]));
  return out;
}

/** What the form and Overblik show: the server's value where it has the key, else the local copy. */
export function mergeExtras(project: ProjectInfo | null | undefined, local: SagsdataExtras): SagsdataExtras {
  const server = serverExtras(project);
  const out: SagsdataExtras = {};
  for (const key of EXTRA_KEYS) setExtra(out, key, serverHas(project, key) ? server[key] : local[key]);
  return out;
}

/** Whether the server stores every Sagsdata key (ruxd-6tz returns them all, null when unset). */
export function serverStoresExtras(project: ProjectInfo | null | undefined): boolean {
  return EXTRA_KEYS.every((key) => serverHas(project, key));
}

/**
 * Values kept in this browser for keys the server now stores but has empty
 * (typed against an older server): what the form brings back as an unsaved
 * change, so they reach the server with the next Gem instead of being lost.
 */
export function leftoverExtras(project: ProjectInfo | null | undefined, local: SagsdataExtras): SagsdataExtras {
  const server = serverExtras(project);
  const out: SagsdataExtras = {};
  for (const key of EXTRA_KEYS) {
    if (serverHas(project, key) && server[key] === undefined && local[key] !== undefined) setExtra(out, key, local[key]);
  }
  return out;
}

/** Keys whose value exists only in this browser ("ikke gemt af serveren endnu"). */
export function heldLocally(project: ProjectInfo | null | undefined, local: SagsdataExtras): ExtraKey[] {
  return EXTRA_KEYS.filter((key) => local[key] !== undefined && !serverHas(project, key));
}

/**
 * The local copy after a save answered with `body`: a key the server now
 * returns is dropped (the server wins); a key it dropped keeps what was
 * sent (null clears it); untouched keys stay.
 */
export function localAfterSave(local: SagsdataExtras, sent: ExtrasPatch, body: ProjectInfo): SagsdataExtras {
  const out: SagsdataExtras = { ...local };
  for (const key of EXTRA_KEYS) {
    if (serverHas(body, key)) {
      delete out[key];
      continue;
    }
    if (!(key in sent)) continue;
    const v = sent[key];
    setExtra(out, key, v === null || v === undefined ? undefined : extraValue(key, v));
  }
  return out;
}

// ---------------------------------------------------------------- storage --

/** The localStorage key prefix; the full key is `reusex:sagsdata-extras-v1:<projectId>`. */
export const EXTRAS_STORAGE_PREFIX = 'reusex:sagsdata-extras-v1:';

export function extrasStorageKey(projectId: string): string {
  return `${EXTRAS_STORAGE_PREFIX}${projectId}`;
}

export type ExtrasStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** A stored copy, keeping only well-typed known keys; garbage reads as empty. */
export function parseStoredExtras(raw: string | null): SagsdataExtras {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const rec = parsed as Record<string, unknown>;
  const out: SagsdataExtras = {};
  for (const key of EXTRA_KEYS) setExtra(out, key, extraValue(key, rec[key]));
  return out;
}

export function readStoredExtras(store: ExtrasStore | undefined, projectId: string | null | undefined): SagsdataExtras {
  if (!store || !projectId) return {};
  try {
    return parseStoredExtras(store.getItem(extrasStorageKey(projectId)));
  } catch {
    return {};
  }
}

/** Writes the local copy; an empty copy removes the key. Storage failures are swallowed. */
export function writeStoredExtras(store: ExtrasStore | undefined, projectId: string, extras: SagsdataExtras): void {
  if (!store) return;
  try {
    const key = extrasStorageKey(projectId);
    if (Object.keys(extras).length === 0) store.removeItem(key);
    else store.setItem(key, JSON.stringify(extras));
  } catch {
    // Private mode / quota / denied — the value simply will not persist.
  }
}

// ------------------------------------------------------------ BEK 496 ------

/** From this demolished floor area a demolition plan is required (BEK 496). */
export const BEK_AREA_LIMIT = 250;
/** The plan reaches the municipality at the latest this many days before demolition. */
export const PLAN_LEAD_DAYS = 14;

export interface BekNote {
  /** A demolition plan and a resource mapping are required. */
  required: boolean;
  /** The bold lead ("≥ 250 m²"). */
  lead: string;
  text: string;
  /** For a one-line summary ("312 m² · plan påkrævet"). */
  short: string;
}

/** What the demolished floor area means under BEK 496; null when it is not known. */
export function bekNote(area: number | null | undefined): BekNote | null {
  if (area === null || area === undefined || !Number.isFinite(area)) return null;
  if (area >= BEK_AREA_LIMIT) {
    return {
      required: true,
      lead: `≥ ${BEK_AREA_LIMIT} m²`,
      text: `Nedrivningsplan og ressourcekortlægning er påkrævet jf. BEK 496. Planen indsendes senest ${PLAN_LEAD_DAYS} dage før nedrivningen.`,
      short: 'plan påkrævet',
    };
  }
  return {
    required: false,
    lead: `Under ${BEK_AREA_LIMIT} m²`,
    text: 'Kun anmeldelse af byggeaffald. Ressourcekortlægning er frivillig.',
    short: 'kun anmeldelse',
  };
}

/** The plan deadline: demolition start − 14 days (ISO), or null without a valid start. */
export function deadline(demolitionStart: string | null | undefined, days: number = PLAN_LEAD_DAYS): string | null {
  if (!demolitionStart || !isIsoDate(demolitionStart)) return null;
  const [y, m, d] = demolitionStart.trim().split('-').map(Number);
  const at = new Date(Date.UTC(y, m - 1, d - days));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`;
}

/** Whole calendar days from `now`'s local date to an ISO date (negative: past); null if not a date. */
export function daysUntil(iso: string, now: Date = new Date()): number | null {
  if (!isIsoDate(iso)) return null;
  const [y, m, d] = iso.trim().split('-').map(Number);
  const target = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

/** How near a deadline `days` away is ("om 5 dage", "fristen er overskredet"); null beyond a month. */
export function daysNote(days: number): { note: string; tone?: 'warn' | 'crit' } | null {
  if (days < 0) return { note: 'fristen er overskredet', tone: 'crit' };
  if (days === 0) return { note: 'i dag', tone: 'warn' };
  if (days === 1) return { note: 'i morgen', tone: 'warn' };
  if (days <= 30) return { note: `om ${days} dage`, tone: days <= 7 ? 'warn' : undefined };
  return null;
}

export function formatArea(area: number): string {
  return `${formatNumber(area, 1)} m²`;
}

// ------------------------------------------------------------ completeness --

/** What the report needs from Sagsdata (prototype: "BBR, matrikel, bygherre" + the frister). */
export const REQUIRED_FOR_REPORT: readonly SagsKey[] = [
  'building_address',
  'bbr_number',
  'cadastral',
  'municipality',
  'client',
  'demolition_start',
  'demolished_floor_area_m2',
];

/** The report-relevant fields still empty, as specs (in form order). */
export function missingFields(project: ProjectInfo | null | undefined, extras: SagsdataExtras): SagsFieldSpec[] {
  const draft = draftOf(project, extras);
  return SAGSDATA_FIELDS.filter((s) => REQUIRED_FOR_REPORT.includes(s.key) && draft[s.key].trim() === '');
}
